import { Center } from '@/components/ui/center'
import { Spinner } from '@/components/ui/spinner'
import {
  fetchCoversByKeywordThunk,
  resetCoversMetaStateOfSearch,
  selectCoversMetaState,
} from '@/redux/slices/coverSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { BaseFileEnum } from '@/typings/domain'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import CoverList from './CoverList'

type CoversByKeywordProps = {
  keyword: string
}

export default function CoversByKeyword({ keyword }: CoversByKeywordProps) {
  const { t } = useTranslation('covers')
  const dispatch = useAppDispatch()
  const formCoversLoaded = useAppSelector(
    state => selectCoversMetaState(state, { byKeyword: true }, BaseFileEnum.Form).loaded
  )
  const formCoverIds = useAppSelector(
    state => selectCoversMetaState(state, { byKeyword: true }, BaseFileEnum.Form).coverIds
  )
  const folderCoverIds = useAppSelector(
    state => selectCoversMetaState(state, { byKeyword: true }, BaseFileEnum.FormFolder).coverIds
  )
  const isEmpty = formCoverIds.length === 0 && folderCoverIds.length === 0

  const refetchCovers = useCallback(() => {
    dispatch(fetchCoversByKeywordThunk({ keyword, type: BaseFileEnum.Form, reset: true }))
  }, [dispatch, keyword])

  useEffect(() => {
    keyword && refetchCovers()
  }, [keyword, refetchCovers])

  useEffect(() => {
    return () => {
      dispatch(resetCoversMetaStateOfSearch())
    }
  }, [dispatch])

  const handleLoadMore = useCallback(
    (type: BaseFileEnum) => {
      dispatch(fetchCoversByKeywordThunk({ keyword, type }))
    },
    [dispatch, keyword]
  )

  if (!keyword) {
    return null
  }

  if (!formCoversLoaded) {
    return (
      <Center className="h-[20vh]">
        <Spinner />
      </Center>
    )
  }

  if (isEmpty) {
    return (
      <View className="items-center justify-center flex-1">
        <EmptyStateIllustration message={t('empty_search')} />
      </View>
    )
  }

  return <CoverList byKeyword includeFolder={false} onLoadMore={handleLoadMore} onRefresh={refetchCovers} />
}
